export interface EngineeringGuide {
  id: string;
  title: string;
  tag: string;
  summary: string;
  content: string;
}

export const ENGINEERING_GUIDES: EngineeringGuide[] = [
  {
    id: "guide-1",
    title: "How to Size Water Supply Pipes (BS EN 806 & CIBSE Guide G)",
    tag: "Plumbing Code",
    summary: "Step-by-step sizing principles for domestic cold and hot water services according to British Standards.",
    content: `
      <p>Proper sizing of water services is critical to ensure adequate pressure and flow rate at all discharge appliances while avoiding excessive noise and erosion.</p>
      <h4 class="font-bold text-slate-900 mt-2">1. The Continuity Principle</h4>
      <p>At any cross section in a closed fluid circuit, volumetric flow rate equals the product of flow velocity and cross-sectional area: <code>Q = v × A</code>. Rearranging for required internal pipe diameter gives:</p>
      <div class="p-2 bg-slate-100 rounded font-mono text-xs">D = sqrt(4Q / (π × v))</div>
      <h4 class="font-bold text-slate-900 mt-2">2. Loading Units and Simultaneous Demand</h4>
      <p>Plumbing systems do not operate with all taps open simultaneously. BS EN 806-3 and CIBSE Guide G assign Loading Units (LU) to each fixture and use empirical conversion curves to determine design peak flow.</p>
    `
  },
  {
    id: "guide-2",
    title: "Darcy-Weisbach vs Hazen-Williams: Which Equation to Use?",
    tag: "Fluid Mechanics",
    summary: "Comparison of the dimensionally homogeneous Darcy-Weisbach equation versus empirical Hazen-Williams.",
    content: `
      <p>While the Hazen-Williams equation is historically popular in municipal waterworks for cold water (around 15°C), it has significant limitations for modern building engineering:</p>
      <ul class="list-disc pl-5 space-y-1">
        <li><strong>Darcy-Weisbach:</strong> Universally valid for all fluids, temperatures, and pipe materials. Directly accounts for fluid viscosity, Reynolds number, and absolute wall roughness.</li>
        <li><strong>Hazen-Williams:</strong> Strictly valid only for water between 5°C and 25°C in turbulent flow. Inaccurate for hot water (50°C-80°C) and glycol mixtures.</li>
      </ul>
      <p>Consulting engineers recommend Darcy-Weisbach as the professional standard for building services design.</p>
    `
  },
  {
    id: "guide-3",
    title: "Velocity Limits in Domestic and Commercial Water Services",
    tag: "Design Standards",
    summary: "Recommended design velocity limits to avoid water hammer, acoustic noise, and pipe erosion.",
    content: `
      <p>Designing pipes with too high a velocity leads to audible whistling, accelerated wall erosion, and dangerous pressure surges (water hammer). Designing with too low a velocity increases installation costs and Legionella risk:</p>
      <ul class="list-disc pl-5 space-y-1">
        <li><strong>Domestic Hot Water (>50°C):</strong> 1.0 – 1.2 m/s maximum (to prevent accelerated erosion-corrosion of copper).</li>
        <li><strong>Domestic Cold Water:</strong> 1.2 – 2.0 m/s (CIBSE recommends 1.5 m/s for general distribution).</li>
        <li><strong>Pump Suction Lines:</strong> 0.8 – 1.2 m/s (to avoid NPSH cavitation).</li>
        <li><strong>Pump Discharge Lines:</strong> 1.8 – 2.4 m/s.</li>
        <li><strong>Minimum Stagnation Threshold:</strong> 0.5 m/s (prevents sediment deposition and biofilm formation).</li>
      </ul>
    `
  },
  {
    id: "guide-4",
    title: "Copper Pipe Sizing: 15mm vs 22mm vs 28mm Flow Capacities",
    tag: "Copper Piping",
    summary: "Practical capacities and application rules for standard half-hard copper tube sizes.",
    content: `
      <p>In standard BS EN 1057 Table X copper tube:</p>
      <ul class="list-disc pl-5 space-y-1">
        <li><strong>15 mm (ID 13.6 mm):</strong> Ideal for individual branch supplies to basins, kitchen sinks, and single showers (flow up to 0.22 L/s at 1.5 m/s).</li>
        <li><strong>22 mm (ID 20.2 mm):</strong> Standard for bath feeds, combi boiler cold feeds, and sub-mains (flow up to 0.48 L/s at 1.5 m/s).</li>
        <li><strong>28 mm (ID 26.2 mm):</strong> Main distribution headers, commercial domestic feeds, and boiler primary circuits (flow up to 0.81 L/s at 1.5 m/s).</li>
        <li><strong>35 mm (ID 32.6 mm):</strong> Multiple residential apartment risers (flow up to 1.25 L/s at 1.5 m/s).</li>
      </ul>
    `
  },
  {
    id: "guide-5",
    title: "Calculating Reynolds Number & Swamee-Jain Friction Factor",
    tag: "Equations",
    summary: "Direct explicit calculation of friction factor f without iterative Colebrook-White charts.",
    content: `
      <p>The Reynolds number indicates the ratio of inertial forces to viscous forces:</p>
      <div class="p-2 bg-slate-100 rounded font-mono text-xs">Re = (v × D) / ν</div>
      <p>Where ν is kinematic viscosity (1.004 × 10⁻⁶ m²/s for water at 20°C). When Re > 4000, flow is fully turbulent. The Swamee-Jain equation directly calculates the Darcy friction factor f within 1% accuracy of Colebrook-White:</p>
      <div class="p-2 bg-slate-100 rounded font-mono text-xs">f = 0.25 / [log10((ε / (3.7 × D)) + (5.74 / Re^0.9))]²</div>
    `
  },
  {
    id: "guide-6",
    title: "Estimating Minor Losses in Pipe Fittings (K-Factor Method)",
    tag: "Loss Analysis",
    summary: "How to account for pressure drops caused by elbows, tees, check valves, and strainers.",
    content: `
      <p>Fittings create turbulence that causes localized head loss: <code>h_m = K × (v² / (2g))</code>.</p>
      <p>Common loss coefficients (K):</p>
      <ul class="list-disc pl-5 space-y-1 text-xs">
        <li>90° Standard Radius Elbow: K = 0.75</li>
        <li>90° Long Radius Elbow: K = 0.45</li>
        <li>45° Elbow: K = 0.35</li>
        <li>Standard Tee (through run): K = 0.40</li>
        <li>Standard Tee (through branch): K = 1.50</li>
        <li>Full Bore Ball Valve (open): K = 0.05</li>
        <li>Swing Check Valve: K = 2.00</li>
      </ul>
    `
  },
  {
    id: "guide-7",
    title: "Water Temperature Effects on Viscosity, Density & Pressure Drop",
    tag: "Thermodynamics",
    summary: "Why hot water heating lines experience lower friction drop than cold water at identical flow velocities.",
    content: `
      <p>As water temperature increases from 10°C to 70°C:</p>
      <ul class="list-disc pl-5 space-y-1">
        <li>Density decreases from 999.7 kg/m³ to 977.8 kg/m³.</li>
        <li>Dynamic viscosity drops drastically from 1.307 mPa·s to 0.404 mPa·s (over a 68% decrease!).</li>
        <li>Because viscosity drops, Reynolds number increases, lowering the Darcy friction factor f. Consequently, hot water circuits experience 15% to 25% lower head loss than cold water for identical pipe geometry.</li>
      </ul>
    `
  },
  {
    id: "guide-8",
    title: "Preventing Water Hammer and Hydraulic Surge Pressure",
    tag: "Safety & Surge",
    summary: "Understanding Joukowsky shock waves caused by fast-acting solenoid valves and quarter-turn taps.",
    content: `
      <p>When fluid in motion is abruptly stopped by a valve closure, kinetic energy transforms into a high-pressure acoustic surge wave known as water hammer. The peak pressure rise is described by the Joukowsky equation:</p>
      <div class="p-2 bg-slate-100 rounded font-mono text-xs">ΔP_surge = ρ × a × Δv</div>
      <p>Where <code>a</code> is acoustic wave speed in the pipe (typically 1000–1300 m/s for metal pipes). Keeping design velocity below 2.0 m/s and installing certified expansion arrestors eliminates hydraulic shock.</p>
    `
  },
  {
    id: "guide-9",
    title: "Hydronic Heating & Chilled Water Pipe Sizing Rules",
    tag: "HVAC Engineering",
    summary: "ASHRAE and CIBSE recommendations for closed-loop hydronic distribution loops.",
    content: `
      <p>In closed-loop HVAC hydronic circuits (chilled water 6°C/12°C and heating 70°C/50°C), pipe sizing balances pump electrical operating costs against initial piping capital expense.</p>
      <p>Standard design criteria:</p>
      <ul class="list-disc pl-5 space-y-1">
        <li>Target pressure drop gradient: <strong>100 to 300 Pa/m</strong> (10 to 30 kPa per 100m).</li>
        <li>Maximum velocity in occupied spaces: <strong>1.2 to 1.5 m/s</strong> (avoids flow noise through radiator and fan coil valves).</li>
        <li>Main plant room headers: up to <strong>2.5 m/s</strong>.</li>
      </ul>
    `
  },
  {
    id: "guide-10",
    title: "Metric Nominal Bore (DN) vs Imperial NPS: Understanding Actual Pipe Dimensions",
    tag: "Standards Guide",
    summary: "Why nominal size is not actual internal diameter and how to convert without sizing errors.",
    content: `
      <p>One of the most frequent errors in mechanical engineering is assuming that nominal pipe diameter represents the actual internal bore.</p>
      <p>For example, a nominal 2" (DN50) Schedule 40 carbon steel pipe has an outside diameter of 60.3 mm and a wall thickness of 3.91 mm, yielding an internal diameter of 52.5 mm (2.07 inches). In contrast, Schedule 80 heavy wall 2" pipe has an internal diameter of only 49.3 mm.</p>
      <p>Always base hydraulic calculations on verified internal bore dimensions rather than nominal names.</p>
    `
  }
];
